<template>
  <div class="page-container um-page">
    <v-snackbar v-model="showSnackBar" :color="snackbarColor" top right timeout="3500">
      <v-icon left color="white">{{ snackbarIcon }}</v-icon>
      {{ msg }}
      <template v-slot:action>
        <v-btn icon small @click="showSnackBar = false"><v-icon color="white">mdi-close</v-icon></v-btn>
      </template>
    </v-snackbar>

    <!-- Hero -->
    <section class="um-hero um-hero--alert">
      <div class="um-hero-top">
        <div class="um-hero-heading">
          <div class="um-hero-icon"><v-icon size="28">mdi-account-multiple-alert-outline</v-icon></div>
          <div class="um-hero-text">
            <div class="um-hero-eyebrow">Users &amp; access</div>
            <h1 class="um-hero-title">Duplicate Accounts</h1>
            <p class="um-hero-subtitle">Accounts that share a mobile number or email address. Edit, deactivate or delete the extra ones.</p>
          </div>
        </div>
        <div class="um-hero-actions">
          <router-link :to="allUsersRoute" class="um-hero-btn">
            <v-icon size="18">mdi-account-group-outline</v-icon>
            <span>All users</span>
          </router-link>
          <button type="button" class="um-hero-btn um-hero-btn--solid" @click="fetchGroups(page)">
            <v-icon size="18">mdi-refresh</v-icon>
            <span>Refresh</span>
          </button>
        </div>
      </div>

      <div class="um-kpis">
        <div v-for="k in kpis" :key="k.label" class="um-kpi">
          <div class="um-kpi-icon" :class="'um-kpi-icon--' + k.tone"><v-icon size="20">{{ k.icon }}</v-icon></div>
          <div class="um-kpi-text">
            <div class="um-kpi-value">{{ formatNumber(k.value) }}</div>
            <div class="um-kpi-label">{{ k.label }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- Groups -->
    <section class="surface um-results">
      <div class="um-results-head">
        <div class="um-results-heading">
          <div class="um-results-title">{{ type === 'mobile' ? 'Shared mobile numbers' : 'Shared email addresses' }}</div>
          <div class="um-results-sub">
            <b>{{ formatNumber(totalGroups) }}</b> {{ totalGroups === 1 ? 'group' : 'groups' }}
            <span v-if="totalPages > 1"> · page {{ page }} of {{ totalPages }}</span>
            <span v-if="loading" class="um-updating">
              <v-progress-circular indeterminate size="12" width="2" color="#15803d" />
              Updating
            </span>
          </div>
        </div>

        <div class="um-toolbar">
          <v-text-field
            v-model="search"
            class="um-toolbar-search"
            outlined
            dense
            hide-details
            clearable
            aria-label="Search duplicates"
            :placeholder="type === 'mobile' ? 'Search number, name, office or email' : 'Search email, name, office or phone'"
            prepend-inner-icon="mdi-magnify"
            @input="onSearchInput"
          />
          <div class="um-segment" role="group" aria-label="Match by">
            <button
              v-for="t in typeOptions"
              :key="t.value"
              type="button"
              class="um-segment-btn"
              :class="{ 'um-segment-btn--active': type === t.value }"
              :aria-pressed="type === t.value ? 'true' : 'false'"
              @click="setType(t.value)"
            >
              <v-icon size="16">{{ t.icon }}</v-icon>{{ t.text }}
              <span class="um-segment-count">{{ formatNumber(t.count) }}</span>
            </button>
          </div>
        </div>
      </div>

      <div v-if="loading && !groups.length" class="pa-4">
        <v-skeleton-loader type="table-row@4"></v-skeleton-loader>
      </div>

      <div v-else-if="!groups.length" class="empty-state">
        <v-icon size="52">mdi-check-decagram-outline</v-icon>
        <div class="cell-strong">{{ searchText ? 'No duplicates match this search' : 'No duplicate accounts' }}</div>
        <div v-if="searchText">Change the search or clear it.</div>
        <div v-else>No two accounts share {{ type === 'mobile' ? 'a mobile number' : 'an email address' }}.</div>
        <div v-if="searchText" class="um-empty-actions">
          <v-btn depressed class="btn-act btn-act--sm btn-act--primary" @click="clearSearch">
            <v-icon left small>mdi-filter-remove-outline</v-icon>Clear search
          </v-btn>
        </div>
      </div>

      <div v-else class="dup-groups">
        <article v-for="(group, gIdx) in groups" :key="gIdx + '-' + group.identifier" class="dup-group">
          <header class="dup-group-head">
            <div class="dup-group-id">
              <span class="dup-group-icon"><v-icon size="20">{{ type === 'mobile' ? 'mdi-phone-outline' : 'mdi-email-outline' }}</v-icon></span>
              <div class="dup-group-text">
                <div class="dup-group-label">{{ type === 'mobile' ? 'Mobile number' : 'Email address' }}</div>
                <div class="dup-group-value">{{ group.identifier || '—' }}</div>
              </div>
            </div>
            <div class="dup-group-meta">
              <span v-for="r in roleMix(group)" :key="r.uType" :class="['fd-role-badge', r.badge]">
                <v-icon size="13" :color="r.color" style="margin-right:4px">{{ r.icon }}</v-icon>
                {{ r.count }} × {{ r.name }}
              </span>
              <span class="dup-group-count">{{ group.count }} accounts</span>
            </div>
          </header>

          <div class="table-scroll">
            <v-simple-table class="app-table dup-table">
              <thead>
                <tr>
                  <th>Account</th>
                  <th>Role</th>
                  <th>Mobile</th>
                  <th>Email</th>
                  <th>Status</th>
                  <th class="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="user in visibleUsers(group)" :key="user._id">
                  <td data-label="Account">
                    <div class="um-person">
                      <span class="um-avatar" :style="{ '--role': roleColor(user.uType) }">{{ initials(user) }}</span>
                      <div class="um-person-text">
                        <div class="um-person-name">{{ user.officeName || '—' }}</div>
                        <span class="um-username">{{ user.username }}</span>
                      </div>
                    </div>
                  </td>
                  <td data-label="Role">
                    <span :class="['fd-role-badge', roleBadgeClass(user.uType)]">
                      <v-icon size="13" :color="roleColor(user.uType)" style="margin-right:4px">{{ roleIcon(user.uType) }}</v-icon>
                      {{ roleName(user.uType) }}
                    </span>
                  </td>
                  <td data-label="Mobile">
                    <span v-if="user.mobNumber" :class="{ 'dup-match': type === 'mobile' }">{{ user.mobNumber }}</span>
                    <span v-else class="fd-cell-dash">—</span>
                  </td>
                  <td data-label="Email">
                    <a
                      v-if="user.emailId"
                      :href="'mailto:' + user.emailId"
                      class="fd-cell-email"
                      :class="{ 'dup-match': type === 'email' }"
                      :title="user.emailId"
                    >{{ user.emailId }}</a>
                    <span v-else class="fd-cell-dash">—</span>
                  </td>
                  <td data-label="Status">
                    <button
                      type="button"
                      :class="['fd-status-pill', user.status === 'Active' ? 'status-active' : 'status-inactive']"
                      title="Click to toggle status"
                      @click="toggleStatus(user)"
                    >
                      <span class="fd-status-dot"></span>
                      {{ user.status === 'Active' ? 'Active' : 'Inactive' }}
                    </button>
                  </td>
                  <td class="app-table-actions text-right">
                    <div class="row-actions">
                      <button type="button" class="um-card-btn um-card-btn--edit" @click="$refs.dialogs.openEdit(user)">
                        <v-icon size="16">mdi-pencil-outline</v-icon>Edit
                      </button>
                      <button type="button" class="um-card-btn um-card-btn--pass" @click="$refs.dialogs.openPassword(user)">
                        <v-icon size="16">mdi-key-variant</v-icon>Password
                      </button>
                      <button type="button" class="um-card-btn um-card-btn--delete" @click="$refs.dialogs.openDelete(user)">
                        <v-icon size="16">mdi-delete-outline</v-icon>Delete
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </div>

          <button
            v-if="group.users.length > previewCount"
            type="button"
            class="dup-more"
            :aria-expanded="isExpanded(group) ? 'true' : 'false'"
            @click="toggleExpanded(group)"
          >
            <v-icon size="18">{{ isExpanded(group) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
            {{ isExpanded(group) ? 'Show fewer' : 'Show all ' + group.users.length + ' accounts' }}
          </button>
        </article>
      </div>

      <div v-if="totalPages > 1" class="pagination-bar">
        <v-pagination v-model="page" :length="totalPages" :total-visible="7" color="#15803d" @input="fetchGroups" />
      </div>
    </section>

    <UserFormDialogs
      ref="dialogs"
      :circles="circlesList"
      :divisions="divisionsList"
      @notify="showNotification"
      @saved="onSaved"
    />
  </div>
</template>

<script>
import axios from "axios";
import UserFormDialogs from "./UserFormDialogs.vue";
import { userPage, roleName, roleIcon, roleColor, roleBadgeClass } from "./userRoles";

// Accounts that share a mobile number or email address, in groups. Each
// account can be edited, given a new password, deactivated or deleted here.
const PAGE_SIZE = 10;

export default {
  name: "DuplicateAccounts",
  components: { UserFormDialogs },
  data() {
    return {
      loading: false,
      msg: "",
      showSnackBar: false,
      snackbarColor: "#0d3320",
      snackbarIcon: "mdi-check-circle",

      type: "mobile",
      search: "",
      searchTimeout: null,
      page: 1,
      totalGroups: 0,
      totalPages: 1,
      fetchSeq: 0,
      groups: [],
      summary: {
        totalAffected: 0,
        mobileGroups: 0,
        emailGroups: 0,
        mobileAffected: 0,
        emailAffected: 0,
      },

      // A group shows its first few accounts until it is expanded
      previewCount: 6,
      expanded: {},

      allUsersRoute: userPage("all").route,
      circlesList: [],
      divisionsList: [],
    };
  },

  computed: {
    searchText() {
      return (this.search || "").trim();
    },

    kpis() {
      const s = this.summary;
      return [
        { label: "Accounts sharing a mobile", value: s.mobileAffected, icon: "mdi-cellphone-link", tone: "alert" },
        { label: "Shared mobile numbers", value: s.mobileGroups, icon: "mdi-phone-outline", tone: "staff" },
        { label: "Accounts sharing an email", value: s.emailAffected, icon: "mdi-email-multiple-outline", tone: "alert" },
        { label: "Shared email addresses", value: s.emailGroups, icon: "mdi-email-outline", tone: "info" },
      ];
    },

    typeOptions() {
      return [
        { value: "mobile", text: "Mobile number", icon: "mdi-phone-outline", count: this.summary.mobileGroups },
        { value: "email", text: "Email", icon: "mdi-email-outline", count: this.summary.emailGroups },
      ];
    },
  },

  mounted() {
    this.fetchHierarchy();
    this.fetchGroups(1);
  },

  methods: {
    roleName,
    roleIcon,
    roleColor,
    roleBadgeClass,

    showNotification(message, isSuccess = true) {
      this.msg = message;
      this.snackbarColor = isSuccess ? "#0d3320" : "#991b1b";
      this.snackbarIcon = isSuccess ? "mdi-check-circle" : "mdi-alert-circle";
      this.showSnackBar = true;
    },

    formatNumber(n) {
      return Number(n || 0).toLocaleString("en-IN");
    },

    initials(user) {
      const words = String(user.officeName || user.username || "")
        .replace(/[^A-Za-z0-9 ]/g, " ")
        .split(" ")
        .filter(Boolean);
      if (!words.length) return "?";
      return (words[0][0] + (words.length > 1 ? words[1][0] : "")).toUpperCase();
    },

    // How many accounts of each role a group holds
    roleMix(group) {
      const counts = {};
      group.users.forEach((u) => {
        const key = String(u.uType);
        counts[key] = (counts[key] || 0) + 1;
      });
      return Object.keys(counts).map((uType) => ({
        uType,
        count: counts[uType],
        name: roleName(uType),
        icon: roleIcon(uType),
        color: roleColor(uType),
        badge: roleBadgeClass(uType),
      }));
    },

    groupKey(group) {
      return this.type + ":" + group.identifier;
    },

    isExpanded(group) {
      return !!this.expanded[this.groupKey(group)];
    },

    toggleExpanded(group) {
      this.$set(this.expanded, this.groupKey(group), !this.isExpanded(group));
    },

    visibleUsers(group) {
      return this.isExpanded(group) ? group.users : group.users.slice(0, this.previewCount);
    },

    setType(value) {
      if (this.type === value) return;
      this.type = value;
      this.fetchGroups(1);
    },

    onSearchInput() {
      if (this.searchTimeout) clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.fetchGroups(1);
      }, 350);
    },

    clearSearch() {
      this.search = "";
      this.fetchGroups(1);
    },

    fetchHierarchy() {
      axios({
        method: "GET",
        url: "/superadmin/hierarchy-options",
        headers: { token: localStorage.getItem("token") },
      })
        .then((res) => {
          if (res.data.status && res.data.data) {
            this.circlesList = res.data.data.circles || [];
            this.divisionsList = res.data.data.divisions || [];
          }
        })
        .catch((err) => {
          console.error("Error fetching hierarchy options:", err);
        });
    },

    fetchGroups(page = 1) {
      this.loading = true;
      this.page = page;
      // Only the latest request may update the list
      const seq = ++this.fetchSeq;
      axios({
        method: "GET",
        url: "/superadmin/duplicates",
        headers: { token: localStorage.getItem("token") },
        params: { type: this.type, page, limit: PAGE_SIZE, search: this.searchText },
      })
        .then((res) => {
          if (seq !== this.fetchSeq) return;
          this.loading = false;
          if (res.data.status && res.data.data) {
            const data = res.data.data;
            this.groups = data.groups || [];
            if (data.summary) this.summary = data.summary;
            if (data.pagination) {
              this.page = data.pagination.page;
              this.totalGroups = data.pagination.totalGroups;
              this.totalPages = data.pagination.totalPages;
            }
          } else {
            this.showNotification(res.data.msg || "Failed to load duplicates", false);
          }
        })
        .catch((err) => {
          if (seq !== this.fetchSeq) return;
          this.loading = false;
          console.error("Error fetching duplicate groups:", err);
          this.showNotification("Network or server error loading duplicates", false);
        });
    },

    toggleStatus(user) {
      axios({
        method: "PUT",
        url: `/superadmin/user/toggle-status/${user._id}`,
        headers: { token: localStorage.getItem("token") },
      })
        .then((res) => {
          if (res.data.status) {
            user.status = res.data.data.status;
            this.showNotification(`User status changed to ${user.status}.`);
          } else {
            this.showNotification(res.data.msg || "Failed to update status", false);
          }
        })
        .catch((err) => {
          console.error("Toggle status error:", err);
          this.showNotification("Server error updating status.", false);
        });
    },

    onSaved(action) {
      if (action !== "password") this.fetchGroups(this.page);
    },
  },
};
</script>

<style scoped src="./userAdmin.css"></style>

<style scoped>
.dup-groups {
  display: grid;
  gap: 16px;
  padding: 16px;
  background: #f6f9f7;
}

.dup-group {
  border-radius: 16px;
  border: 1px solid #fecaca;
  background: #ffffff;
  box-shadow: 0 4px 14px rgba(220, 38, 38, 0.05);
  overflow: hidden;
}

.dup-group-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px 16px;
  padding: 14px 18px;
  border-bottom: 1px solid #fee2e2;
  background: linear-gradient(90deg, #fff5f5 0%, #ffffff 100%);
}

.dup-group-id {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.dup-group-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: #fee2e2;
}

.dup-group-icon .v-icon {
  color: #dc2626 !important;
}

.dup-group-text {
  min-width: 0;
}

.dup-group-label {
  font-size: 11.5px;
  font-weight: 800;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #64748b;
}

.dup-group-value {
  font-size: 16px;
  font-weight: 800;
  color: #991b1b;
  word-break: break-all;
  font-variant-numeric: tabular-nums;
}

.dup-group-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}

.dup-group-count {
  padding: 4px 12px;
  border-radius: 999px;
  background: #dc2626;
  color: #ffffff;
  font-size: 12px;
  font-weight: 800;
  white-space: nowrap;
  box-shadow: 0 4px 10px rgba(220, 38, 38, 0.25);
}

.dup-match {
  font-weight: 700;
  color: #b91c1c !important;
}

.dup-table .row-actions .um-card-btn {
  height: 34px;
}

.dup-more {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  height: 42px;
  border: none;
  border-top: 1px dashed #fecaca;
  background: #fffafa;
  color: #b91c1c;
  font: inherit;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.dup-more .v-icon {
  color: inherit !important;
}

.dup-more:hover {
  background: #fef2f2;
}

@media (max-width: 768px) {
  .dup-groups {
    padding: 12px;
    gap: 12px;
  }

  .dup-group-head {
    padding: 12px 14px;
  }

  .dup-table .row-actions .um-card-btn {
    width: 100%;
  }

  .dup-table .um-person {
    min-width: 0;
    justify-content: flex-end;
  }

  .dup-table .um-person-text {
    align-items: flex-end;
  }
}
</style>
